import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface AseguradoData {
	aseguradoNombre: string
	aseguradoCuit: string
	aseguradoDomicilio: string
}

interface CaucionGeneralAseguradoScreenProps {
	onNext: (data: AseguradoData) => void
	onBack: () => void
	data?: Partial<AseguradoData>
}

export function CaucionGeneralAseguradoScreen({
	onNext,
	onBack,
	data,
}: CaucionGeneralAseguradoScreenProps) {
	const [formData, setFormData] = useState<AseguradoData>({
		aseguradoNombre: data?.aseguradoNombre || '',
		aseguradoCuit: data?.aseguradoCuit || '',
		aseguradoDomicilio: data?.aseguradoDomicilio || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateCUIT = (cuit: string) => {
		const cleaned = cuit.replace(/-/g, '')
		return cleaned.length === 11 && /^\d+$/.test(cleaned)
	}

	const formatCUIT = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (cleaned.length <= 2) return cleaned
		if (cleaned.length <= 10)
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
		return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10, 11)}`
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.aseguradoNombre.trim()) {
			newErrors.aseguradoNombre = 'Ingresa el nombre del asegurado'
		}
		if (!formData.aseguradoCuit.trim()) {
			newErrors.aseguradoCuit = 'Ingresa el CUIT'
		} else if (!validateCUIT(formData.aseguradoCuit)) {
			newErrors.aseguradoCuit = 'El CUIT debe tener 11 dígitos (XX-XXXXXXXX-X)'
		}
		if (!formData.aseguradoDomicilio.trim()) {
			newErrors.aseguradoDomicilio = 'Ingresa el domicilio'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.aseguradoNombre.trim() &&
		formData.aseguradoCuit.trim() &&
		formData.aseguradoDomicilio.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-8">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos del Asegurado
						<br />
						<span className="text-lg text-neutral-600">
							(Entidad Licitante/Contratante)
						</span>
					</h2>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Nombre / Razón Social"
							labelPosition="internal"
							size="l"
							placeholder="Nombre del organismo o entidad"
							value={formData.aseguradoNombre}
							onChange={(e) => {
								setFormData({ ...formData, aseguradoNombre: e.target.value })
								setErrors({ ...errors, aseguradoNombre: '' })
							}}
							errorText={errors.aseguradoNombre}
							required
						/>
						<InputText
							label="CUIT"
							labelPosition="internal"
							size="l"
							placeholder="XX-XXXXXXXX-X"
							value={formData.aseguradoCuit}
							onChange={(e) => {
								setFormData({
									...formData,
									aseguradoCuit: formatCUIT(e.target.value),
								})
								setErrors({ ...errors, aseguradoCuit: '' })
							}}
							errorText={errors.aseguradoCuit}
							required
						/>
						<InputText
							label="Domicilio"
							labelPosition="internal"
							size="l"
							placeholder="Dirección completa"
							value={formData.aseguradoDomicilio}
							onChange={(e) => {
								setFormData({ ...formData, aseguradoDomicilio: e.target.value })
								setErrors({ ...errors, aseguradoDomicilio: '' })
							}}
							errorText={errors.aseguradoDomicilio}
							required
						/>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
